import { useEffect } from "react";

// Falls back to the values baked into public/index.html (see the <head> there) —
// used whenever a page doesn't have something more specific to say.
const DEFAULT_DESCRIPTION =
  "A trusted registry for reusable AI agent skills. Discover, govern, and deploy scanned skills across your organization or the open community.";
const DEFAULT_IMAGE_PATH = "/og-image.png";

interface SeoMetaOptions {
  title: string;
  description?: string;
  // Canonical/OG path for THIS page, e.g. `/skill/${slug}`. Defaults to the current
  // location so every route gets its own canonical instead of inheriting index.html's.
  path?: string;
  image?: string; // absolute or root-relative; defaults to the site's og-image.png
  // Optional JSON-LD structured data (e.g. FAQPage) for this page. Rendered as a
  // <script type="application/ld+json"> tag — invisible, doesn't affect layout/content.
  jsonLd?: object;
  // Keep this page out of search results. MUST match what the origin serves for the same
  // path (see _NOINDEX_PREFIXES / the /skill/{slug}/* branch in backend/app/services/seo.py):
  // Google reads `robots` from the raw HTML *and* again from the rendered DOM, so a page
  // whose two copies disagree gets the more permissive of the two.
  noindex?: boolean;
}

const JSON_LD_ID = "seo-meta-jsonld";

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Per-page replacement for `useDocumentTitle` — sets the title AND keeps the
// description, canonical link, and Open Graph/Twitter tags in sync with the
// current route, instead of every page silently inheriting index.html's
// homepage-only values (which previously told crawlers every page was a
// duplicate of "/", and gave social shares/search results the same generic
// title+summary everywhere).
export function useSeoMeta({ title, description, path, image, jsonLd, noindex }: SeoMetaOptions) {
  useEffect(() => {
    document.title = title;

    const desc = description?.trim() || DEFAULT_DESCRIPTION;
    const url = `${window.location.origin}${path ?? window.location.pathname}`;
    const imageUrl = image
      ? new URL(image, window.location.origin).toString()
      : `${window.location.origin}${DEFAULT_IMAGE_PATH}`;

    upsertMeta("name", "description", desc);
    upsertCanonical(url);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", imageUrl);

    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);
    upsertMeta("name", "twitter:image", imageUrl);

    // The origin injects `<meta name="robots" content="noindex, follow">` for the routes it
    // excludes. On a client-side navigation that tag would otherwise linger into the next
    // page, silently de-indexing whatever the user browsed to next — so an indexable page
    // actively clears it rather than just not setting it.
    if (noindex) upsertMeta("name", "robots", "noindex, follow");
    else document.querySelector('meta[name="robots"]')?.remove();

    if (jsonLd) {
      let script = document.getElementById(JSON_LD_ID) as HTMLScriptElement | null;
      if (!script) {
        script = document.createElement("script");
        script.id = JSON_LD_ID;
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonLd);
    } else {
      document.getElementById(JSON_LD_ID)?.remove();
    }
    return () => {
      document.getElementById(JSON_LD_ID)?.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, path, image, jsonLd, noindex]);
}
